/**
 * @file BannerUploadForm.js
 * @module src/js/components/BannerUploadForm
 * @description
 * **A specialized upload form component for banner photos.**
 * - Allows users to upload one or more banner images.
 * - Validates file selection and enables upload button accordingly.
 * - Redirects to login if user is 'Guest'.
 * 
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useUploadBanner hook. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useUploadBanner from "../hooks/useUploadBanner";
import SpanARow from "./SpanARow";
import ChooseAFile from "./ChooseAFile";
import CanUploadCheck from "./CanUploadCheck";

export default function BannerUploadForm({ setData }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { uid } = useUser();
    const uploadBanner = useUploadBanner();
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [numUploadedImages, setNumUploadedImages] = useState(1);
    const [files, setFiles] = useState(new Array(numUploadedImages).fill(null));
    const [canUpload, setCanUpload] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        setNumUploadedImages(1);
        setFiles([null]);
    }, [showUploadForm]);

    useEffect(() => {
        setFiles(prev => Array.from({ length: numUploadedImages }, (_, i) => prev[i] || null));
    }, [numUploadedImages]);

    return uid === 'Guest' ? (
        <SpanARow
            colSpan="1"
            onClick={() => navigate(
                '/login',
                { state: { from: pathname } }
            )}
            text='🔑 Log in to upload or delete'
        />
    ) : (
        showUploadForm ? (
            <tr>
                <td>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginBottom: '5px',
                        fontSize: '1.1em'
                    }}>
                        <b>Upload</b>
                        <select value={numUploadedImages}
                            onChange={(e) => setNumUploadedImages(e.target.value)}
                            style={{ width: '20%', minWidth: '100px' }}
                        >
                            {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                                <option key={num} value={num}>
                                    {num}
                                </option>
                            ))}
                        </select>
                        <b>{`banner photo${numUploadedImages > 1 ? 's' : ''}`}</b>
                    </span>
                    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {Array.from({ length: numUploadedImages }, (_, i) => (
                            <ChooseAFile
                                key={i}
                                index={i}
                                accept='.jpg, .jpeg, .png'
                                files={files}
                                setFiles={setFiles}
                            />
                        ))}
                    </div>
                    <CanUploadCheck
                        files={files}
                        isAcceptListEmpty={false}
                        setCanUpload={setCanUpload}
                    />
                </td>

                {/* Upload and Cancel Buttons */}
                <td className='hori-vert-center'>
                    <button
                        type="submit"
                        style={{ marginBottom: '15px' }}
                        disabled={!canUpload || uploading}
                        onClick={() => uploadBanner({
                            files,
                            setData,
                            setUploading,
                            setShowUploadForm
                        })}
                    >
                        Upload
                    </button>
                    <button onClick={() => setShowUploadForm(false)} disabled={uploading}>
                        Cancel
                    </button>
                </td>
            </tr>
        ) : (
            <SpanARow
                colSpan="2"
                onClick={() => setShowUploadForm(true)}
                text="📷 Upload banner photos"
            />
        )
    );
}